import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface UserProfile {
  id: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  pseudo: string | null;
  avatar_url: string | null;
  airbnb_verified: boolean;
  airbnb_listing_url: string | null;
  airbnb_rating: number | null;
  exchange_plan: "national" | "europe";
  subscription_plan: string;
  subscription_expires_at: string | null;
  is_founding_member: boolean;
  role: string | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (params: {
    email: string;
    password: string;
    fullName?: string;
    airbnbListingUrl: string;
    airbnbRating: number;
    exchangePlan: "national" | "europe";
  }) => Promise<{ error?: string }>;
  signInWithGoogle: (nextPath?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function buildFallbackProfile(user: User): UserProfile {
  const metadata = (user.user_metadata || {}) as Record<string, unknown>;
  const ratingValue =
    typeof metadata.airbnb_rating === "number"
      ? metadata.airbnb_rating
      : typeof metadata.airbnb_rating === "string"
        ? Number(metadata.airbnb_rating)
        : null;
  const safeRating = Number.isFinite(ratingValue as number)
    ? (ratingValue as number)
    : null;

  return {
    id: user.id,
    full_name:
      typeof metadata.full_name === "string" ? metadata.full_name : null,
    first_name:
      typeof metadata.first_name === "string" ? metadata.first_name : null,
    last_name:
      typeof metadata.last_name === "string" ? metadata.last_name : null,
    pseudo: typeof metadata.pseudo === "string" ? metadata.pseudo : null,
    avatar_url:
      typeof metadata.avatar_url === "string" ? metadata.avatar_url : null,
    airbnb_verified: Boolean(metadata.airbnb_verified),
    airbnb_listing_url:
      typeof metadata.airbnb_listing_url === "string"
        ? metadata.airbnb_listing_url
        : null,
    airbnb_rating: safeRating,
    exchange_plan: metadata.exchange_plan === "europe" ? "europe" : "national",
    subscription_plan:
      typeof metadata.subscription_plan === "string"
        ? metadata.subscription_plan
        : "free",
    subscription_expires_at:
      typeof metadata.subscription_expires_at === "string"
        ? metadata.subscription_expires_at
        : null,
    is_founding_member: Boolean(metadata.is_founding_member),
    role: typeof metadata.role === "string" ? metadata.role : "user",
  };
}

async function fetchProfile(user: User): Promise<UserProfile> {
  const fallback = buildFallbackProfile(user);

  try {
    const [profileResult, adminResult] = await Promise.all([
      supabase
        .from("profiles")
        .select(
          "id, full_name, first_name, last_name, pseudo, avatar_url, airbnb_verified, airbnb_listing_url, airbnb_rating, subscription_plan, subscription_expires_at, is_founding_member",
        )
        .eq("id", user.id)
        .maybeSingle(),
      supabase
        .from("admin_users")
        .select("active")
        .eq("user_id", user.id)
        .maybeSingle(),
    ]);

    if (profileResult.error || !profileResult.data) return fallback;

    const isAdmin = adminResult.data?.active === true;

    return {
      ...fallback,
      ...(profileResult.data as Partial<UserProfile>),
      first_name: profileResult.data.first_name || fallback.first_name,
      last_name: profileResult.data.last_name || fallback.last_name,
      pseudo: profileResult.data.pseudo || fallback.pseudo,
      role: isAdmin ? "admin" : "user",
      id: user.id,
    };
  } catch {
    return fallback;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number) => {
    let timer: number | undefined;
    try {
      return await Promise.race<T | null>([
        promise,
        new Promise<null>((resolve) => {
          timer = window.setTimeout(() => resolve(null), timeoutMs);
        }),
      ]);
    } finally {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    }
  };

  const safeGetSession = useCallback(async () => {
    try {
      const result = await withTimeout<
        Awaited<ReturnType<typeof supabase.auth.getSession>>
      >(supabase.auth.getSession(), 3000);
      return result?.data?.session ?? null;
    } catch {
      return null;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const currentSession = await safeGetSession();

    setSession(currentSession);
    setUser(currentSession?.user ?? null);
    if (currentSession?.user) {
      setProfile(await fetchProfile(currentSession.user));
    } else {
      setProfile(null);
    }
  }, [safeGetSession]);

  useEffect(() => {
    let active = true;
    let profileRequestId = 0;

    const applySession = (nextSession: Session | null) => {
      if (!active) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        setProfile(null);
        return;
      }

      const requestId = ++profileRequestId;
      const currentUser = nextSession.user;
      void fetchProfile(currentUser).then((nextProfile) => {
        if (active && requestId === profileRequestId) {
          setProfile(nextProfile);
        }
      });
    };

    const bootstrap = async () => {
      try {
        const currentSession = await safeGetSession();
        applySession(currentSession);
      } finally {
        if (active) setLoading(false);
      }
    };

    void bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      applySession(nextSession);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [safeGetSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return error ? { error: error.message } : {};
  }, []);

  const signUp = useCallback(
    async ({
      email,
      password,
      fullName,
      airbnbListingUrl,
      airbnbRating,
      exchangePlan,
    }: {
      email: string;
      password: string;
      fullName?: string;
      airbnbListingUrl: string;
      airbnbRating: number;
      exchangePlan: "national" | "europe";
    }) => {
      const redirectTo =
        typeof window !== "undefined"
          ? `${window.location.origin}/auth/callback`
          : undefined;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...(fullName ? { full_name: fullName } : {}),
            airbnb_listing_url: airbnbListingUrl,
            airbnb_rating: airbnbRating,
            exchange_plan: exchangePlan,
            airbnb_verified: false,
          },
          emailRedirectTo: redirectTo,
        },
      });

      return error ? { error: error.message } : {};
    },
    [],
  );

  const signInWithGoogle = useCallback(async (nextPath = "/dashboard") => {
    let redirectTo: string | undefined;
    if (typeof window !== "undefined") {
      const safeNextPath =
        nextPath.startsWith("/") && !nextPath.startsWith("//")
          ? nextPath
          : "/dashboard";
      const next = encodeURIComponent(safeNextPath);
      redirectTo = `${window.location.origin}/auth/callback?next=${next}`;
    }

    // Brave can keep stale auth entries between OAuth attempts.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // Ignore cleanup errors.
    }

    if (typeof window !== "undefined") {
      try {
        Object.keys(window.localStorage).forEach((key) => {
          if (key.startsWith("sb-") && key.includes("-auth-token")) {
            window.localStorage.removeItem(key);
          }
        });
      } catch {
        // Ignore storage errors in privacy modes.
      }
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          access_type: "offline",
          prompt: "select_account",
        },
      },
    });

    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: "local" });
    setProfile(null);
    setUser(null);
    setSession(null);
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const pingPresence = async (source: string) => {
      if (cancelled) return;
      try {
        await supabase.rpc("record_member_presence", { p_source: source });
      } catch {
        // Presence is non-blocking for member UX.
      }
    };

    void pingPresence("web");

    const intervalId = window.setInterval(() => {
      void pingPresence("web");
    }, 90_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void pingPresence("web:visible");
      }
    };

    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      refreshSession,
    }),
    [
      loading,
      profile,
      session,
      user,
      refreshSession,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
